import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, ArrowLeft, Globe, FileText, Layout, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap } from '@/lib/api';
import { pickLocalized, type Localized } from '@/lib/i18n';
import { usePageEditor } from '@/features/page-builder/usePageEditor';
import { PageBuilder } from '@/features/page-builder/PageBuilder';
import { PostClassicEditor } from '@/features/posts/PostClassicEditor';
import { PostTermsPanel } from '@/features/posts/PostTermsPanel';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import type { BlockInstance } from '@sytel/blocks';

interface PostPayload {
  id: string;
  slug: string;
  locale: string;
  title: Localized;
  excerpt: Localized;
  status: 'draft' | 'published' | 'archived';
  data?: Record<string, unknown> | null;
  blocks: BlockInstance[];
  terms?: Array<{ id: string }>;
}

interface PostTypeInfo {
  key: string;
  label_singular: string;
  label_plural: string;
  has_blocks: boolean;
  fields_schema?: Array<{ name: string; label: string; type: string }> | null;
}

export const Route = createFileRoute('/_app/post-types/$key/posts/$id')({
  component: PostEditorView,
});

type EditMode = 'classic' | 'builder' | 'meta';

function PostEditorView() {
  const { key, id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const editor = usePageEditor();
  const initialized = useRef(false);
  const [mode, setMode] = useState<EditMode>('classic');
  const [excerpt, setExcerpt] = useState('');
  const [postData, setPostData] = useState<Record<string, unknown>>({});
  const [termIds, setTermIds] = useState<string[]>([]);
  const [publishing, setPublishing] = useState(false);

  const { data: postType } = useQuery({
    queryKey: ['post-type', key],
    queryFn: () => unwrap<PostTypeInfo>(api.get(`/post-types/${key}`)),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['post', key, id],
    queryFn: () => unwrap<PostPayload>(api.get(`/post-types/${key}/posts/${id}`)),
    enabled: id !== 'new',
  });

  useEffect(() => {
    if (initialized.current) return;
    if (id === 'new') {
      editor.init({
        pageId: null,
        title: '',
        slug: '',
        locale: 'es',
        status: 'draft',
        blocks: [],
      });
      setExcerpt('');
      setPostData({});
      setTermIds([]);
      initialized.current = true;
    } else if (data) {
      editor.init({
        pageId: data.id,
        title: pickLocalized(data.title, data.locale, data.slug),
        slug: data.slug,
        locale: data.locale,
        status: data.status,
        blocks: data.blocks,
      });
      setExcerpt(pickLocalized(data.excerpt, data.locale, ''));
      setPostData(data.data ?? {});
      setTermIds((data.terms ?? []).map((t) => t.id));
      initialized.current = true;
    }
  }, [id, key, data, editor]);

  // Default mode depends on post type: post types without blocks → classic
  useEffect(() => {
    if (postType && !postType.has_blocks) setMode('classic');
  }, [postType?.has_blocks]);

  const save = useMutation({
    mutationFn: async (override?: Partial<{ status: typeof editor.status }>) => {
      if (!editor.title.trim()) {
        throw new Error('El título es obligatorio.');
      }
      const finalSlug = editor.slug.trim() || slugify(editor.title);
      const body: Record<string, unknown> = {
        title: editor.title,
        slug: finalSlug,
        locale: editor.locale,
        status: override?.status ?? editor.status,
        excerpt: excerpt || null,
        data: Object.keys(postData).length ? postData : null,
        term_ids: termIds,
        blocks: editor.blocks.map((b, i) => ({
          type: b.type,
          order: i,
          visible: b.visible,
          data: b.data,
        })),
      };
      if (editor.pageId) {
        return unwrap<PostPayload>(api.put(`/post-types/${key}/posts/${editor.pageId}`, body));
      }
      return unwrap<PostPayload>(api.post(`/post-types/${key}/posts`, body));
    },
    onSuccess: async (saved) => {
      toast.success('Entrada guardada');
      editor.clearDirty();
      await queryClient.invalidateQueries({ queryKey: ['posts', key] });
      await queryClient.invalidateQueries({ queryKey: ['post', key, saved.id] });
      if (!editor.pageId) {
        void navigate({ to: '/post-types/$key/posts/$id', params: { key, id: saved.id } });
      }
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } }; message?: string }).response?.data?.message
        ?? (err as { message?: string }).message;
      toast.error(msg ?? 'Error al guardar');
    },
  });

  async function handlePublish() {
    if (!editor.title.trim()) {
      toast.error('El título es obligatorio para publicar.');
      return;
    }
    setPublishing(true);
    try {
      // Force status=published; this also persists current edits (blocks, etc.).
      editor.setMeta({ status: 'published' });
      await save.mutateAsync({ status: 'published' });
      toast.success('Publicado correctamente');
    } catch {
      // toast already shown by save.onError
    } finally {
      setPublishing(false);
    }
  }

  if (id !== 'new' && isLoading) {
    return <div className="p-8 text-sm text-neutral-500">Cargando…</div>;
  }

  const featuredImage = (postData.cover_image as string) || (editor.advanced.preview_image ?? null);

  return (
    <div className="flex h-screen flex-col">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-200 bg-white px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => void navigate({ to: '/post-types/$key/posts', params: { key } })}
            className="rounded p-1 text-neutral-500 hover:bg-neutral-100"
          >
            <ArrowLeft className="size-4" />
          </button>
          <span className="text-sm text-neutral-500">
            {postType?.label_singular ?? key}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {editor.isDirty && (
            <span className="text-xs text-amber-600">Cambios sin guardar</span>
          )}
          <div className="flex items-center rounded-md border border-neutral-200 bg-neutral-50">
            <ModeBtn active={mode === 'classic'} onClick={() => setMode('classic')}>
              <FileText className="size-3.5" /> Editor
            </ModeBtn>
            {postType?.has_blocks !== false && (
              <ModeBtn active={mode === 'builder'} onClick={() => setMode('builder')}>
                <Layout className="size-3.5" /> Bloques avanzados
              </ModeBtn>
            )}
            <ModeBtn active={mode === 'meta'} onClick={() => setMode('meta')}>
              <Settings className="size-3.5" /> Ajustes
            </ModeBtn>
          </div>
          <Button onClick={() => save.mutate(undefined)} disabled={save.isPending || publishing}>
            <Save className="size-4" /> {save.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
          <Button
            variant="success"
            onClick={handlePublish}
            disabled={save.isPending || publishing}
          >
            <Globe className="size-4" /> {publishing ? 'Publicando…' : 'Publicar'}
          </Button>
        </div>
      </header>

      {mode === 'classic' && (
        <PostClassicEditor
          featuredImage={featuredImage}
          onFeaturedImageChange={(url) => {
            setPostData((d) => ({ ...d, cover_image: url ?? '' }));
            editor.setAdvanced({ preview_image: url });
          }}
          excerpt={excerpt}
          onExcerptChange={setExcerpt}
          customFields={(postType?.fields_schema ?? []).filter((f) => f.name !== 'cover_image')}
          customValues={postData}
          onCustomValuesChange={setPostData}
          termsSlot={<PostTermsPanel postTypeKey={key} value={termIds} onChange={setTermIds} />}
        />
      )}

      {mode === 'builder' && <PageBuilder />}

      {mode === 'meta' && (
        <div className="mx-auto w-full max-w-3xl space-y-6 overflow-y-auto p-8">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-neutral-700">Título</label>
            <input
              value={editor.title}
              onChange={(e) => editor.setMeta({ title: e.target.value })}
              className="w-full rounded-md border border-neutral-200 px-3 py-2 text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-neutral-700">Slug (URL)</label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-400">/{key}/</span>
              <input
                value={editor.slug}
                onChange={(e) => editor.setMeta({ slug: e.target.value })}
                className="flex-1 rounded-md border border-neutral-200 px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-neutral-700">Idioma</label>
            <select
              value={editor.locale}
              onChange={(e) => editor.setMeta({ locale: e.target.value })}
              className="rounded-md border border-neutral-200 px-3 py-2 text-sm"
            >
              <option value="es">Español</option>
              <option value="en">English</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-neutral-700">Extracto / resumen</label>
            <textarea
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              rows={4}
              maxLength={500}
              placeholder="Aparece en listados y como meta description por defecto."
              className="w-full rounded-md border border-neutral-200 px-3 py-2 text-sm"
            />
          </div>
          <div className="space-y-4">
            <PostTermsPanel postTypeKey={key} value={termIds} onChange={setTermIds} />
          </div>
        </div>
      )}
    </div>
  );
}

function ModeBtn({
  active, onClick, children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors first:rounded-l-md last:rounded-r-md',
        active ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:bg-neutral-100',
      )}
    >
      {children}
    </button>
  );
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100) || 'nuevo';
}
